import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Notify Step
 *
 * Sends a plain informational notification — in-app (bell inbox) and/or
 * email — to configured users and user groups. Like the Approval step's
 * notification fan-out, but with no decision gate: nothing blocks, nothing
 * awaits a response.
 *
 * Email mirrors the Approval step's delivery path: sent via Microsoft
 * Graph from the configured `senderMailbox`, with the body rendered from
 * an email template (`builtin:<key>` ref or an org template lineage id)
 * through EmailTemplateService. The in-app row is written via
 * NotificationService (in-app channel only — Graph is the sole email
 * sender, so nothing double-sends).
 *
 * The optional `items` input carries per-run data for the message (ERP
 * order numbers, discount group ids, …). It is array-typed, so an
 * upstream array arrives whole — ONE notification per run, not one per
 * item. Because the orchestrator only treats an empty array as a dead
 * edge for single-value targets, an empty `items` array still runs this
 * step; `skipWhenEmpty` (default true) makes the handler skip it instead
 * of notifying about nothing.
 *
 * Optional resource-privilege scoping: wire the `subjectIds` input to the
 * ids of the entities the message is about and set `subjectKind`, and the
 * recipient list is narrowed to users who can see EVERY subject under
 * customer-scoped privilege enforcement — the same per-user visibility
 * probe the Approval step's notifications use. Unwired = no scoping.
 */
export const notifyStep = defineStepConfig({
  type: "notify",
  name: "Notify",
  description:
    "Sends an informational email and/or in-app notification to picked " +
    "users and groups. No decision required — the workflow continues " +
    "immediately.",
  category: "Integration",

  inputSchema: z.object({
    items: z
      .array(z.unknown())
      .optional()
      .describe(
        "Per-run data for the message (order numbers, entity ids, …). " +
          "Arrays arrive whole (no fanout). Exposed to templates and to " +
          "{{itemCount}}/{{itemsList}} tokens in title/body. When wired " +
          "and empty, the step skips unless skipWhenEmpty is off.",
      ),
    subjectIds: z
      .array(z.string().uuid())
      .optional()
      .describe(
        "Ids of the entities this notification is about, used for " +
          "resource-privilege recipient scoping (requires subjectKind in " +
          "the config). Arrays arrive whole (no fanout); a scalar " +
          "upstream id coerces in. Unwired = no scoping.",
      ),
  }),

  outputSchema: z.object({
    skipped: z
      .boolean()
      .describe(
        "True when the step sent nothing (empty items with skipWhenEmpty, " +
          "or no recipients resolved).",
      ),
    recipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Distinct users targeted after group expansion + dedupe."),
    emailRecipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Recipients with a resolvable email address."),
    emailSent: z
      .boolean()
      .describe(
        "True when the Graph email was handed off successfully. False " +
          "when email wasn't requested, no addresses resolved, or the " +
          "send failed (send failures log but never fail the step).",
      ),
  }),

  configSchema: z
    .object({
      recipientUserIds: z
        .array(z.string().uuid())
        .optional()
        .describe("Specific users to notify (unioned with group members)."),

      recipientUserGroupIds: z
        .array(z.string().uuid())
        .optional()
        .describe(
          "User groups to notify — members resolve at send time, so " +
            "group changes are picked up without re-saving the workflow.",
        ),

      title: z
        .string()
        .min(1)
        .describe(
          "In-app headline. Supports {{itemCount}} and {{itemsList}} tokens.",
        ),
      body: z
        .string()
        .min(1)
        .describe(
          "In-app body text. Supports {{itemCount}} and {{itemsList}} tokens.",
        ),

      channels: z
        .array(z.enum(["in_app", "email"]))
        .min(1)
        .default(["in_app", "email"])
        .describe(
          "Delivery channels. 'in_app' writes to the bell inbox; 'email' " +
            "sends via Microsoft Graph from senderMailbox.",
        ),

      senderMailbox: z
        .preprocess(
          // The editor persists an empty-string default for the UPN field.
          // Coerce those into `undefined` so `.optional()` accepts them
          // instead of `.min(1)` rejecting at run time.
          (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
          z.string().min(1).optional(),
        )
        .describe(
          "Mailbox UPN to send from when 'email' is in channels. Must be " +
            "an enabled UPN under one of the org's EmailIngestConfigs.",
        ),

      emailTemplateId: z
        .string()
        .optional()
        .describe(
          "Email template ref: 'builtin:<key>' or an org email-template " +
            "lineage id. When unset, the email falls back to the " +
            "interpolated title/body wrapped in simple HTML.",
        ),

      emailSubject: z
        .string()
        .optional()
        .describe(
          "Email subject when no template is selected (a template owns " +
            "its own subject). Defaults to the interpolated title.",
        ),

      skipWhenEmpty: z
        .boolean()
        .default(true)
        .describe(
          "Skip (send nothing) when the wired items input resolves to an " +
            "empty array — e.g. no ERP order numbers landed, or zero " +
            "groups were created.",
        ),

      subjectKind: z
        .enum(["draft_sales_order", "discount_group"])
        .optional()
        .describe(
          "Resource-privilege scoping: what kind of entity the wired " +
            "subjectIds input refers to. When set and subjectIds arrive, " +
            "recipients are narrowed to users who can see EVERY subject " +
            "under customer-scoped privilege enforcement (admins and " +
            "enforcement-disabled users always pass). Values mirror " +
            "NotificationSubjectKind in the backend resource-privileges " +
            "service. Unset = no scoping.",
        ),
    })
    // Catch the "configured but empty" case at validation time — without
    // these checks a run would silently notify zero recipients (or fail
    // the Graph send) and look successful on the canvas.
    .superRefine((val, ctx) => {
      const hasUsers = (val.recipientUserIds?.length ?? 0) > 0;
      const hasGroups = (val.recipientUserGroupIds?.length ?? 0) > 0;
      if (!hasUsers && !hasGroups) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recipientUserIds"],
          message:
            "At least one recipient user or user group must be configured.",
        });
      }
      if (val.channels.includes("email") && !val.senderMailbox) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["senderMailbox"],
          message: "senderMailbox is required when 'email' is in channels.",
        });
      }
    }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "1 minute",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
